<template>
  <div class="social-panel stack tight">
    <UiPanel tone="social">
      <div class="header">
        <div style="min-width: 0;">
          <h3 class="title">{{ ICONS.sessions.social }} Relationships</h3>
          <div class="subtitle text-muted">
            Opinion and area reputation drive pricing, hostility, and shop access.
          </div>
        </div>
        <UiChip v-if="opinionsFreshness" :variant="opinionsFreshness.variant" class="stat-mono num">
          {{ opinionsFreshness.label }}
        </UiChip>
      </div>

      <div class="controls">
        <label class="search">
          <span class="sr-only">Search NPC opinions</span>
          <input v-model="query" class="ui-input" placeholder="Search…" />
        </label>
      </div>
    </UiPanel>

    <UiCallout v-if="error" variant="danger">{{ error }}</UiCallout>
    <UiCallout v-else-if="loading" variant="info">Loading opinions…</UiCallout>
    <UiCallout v-else-if="filteredOpinions.length === 0" variant="muted">No NPCs encountered yet.</UiCallout>

    <UiPanel v-else tone="social">
      <UiSectionHeader>
        <template #title>NPC Opinions</template>
      </UiSectionHeader>

      <div class="list">
        <div
          v-for="op in filteredOpinions"
          :key="op.npcStateId"
          class="opinion-row"
          :class="{ focused: op.npcStateId === focusNpcStateId }"
          :data-npc-id="op.npcId"
          :data-npc-state-id="op.npcStateId"
        >
          <div class="opinion-head">
            <div class="icon-well" aria-hidden="true">{{ ICONS.inspectKinds.npc }}</div>
            <div class="opinion-main">
              <div class="opinion-top">
                <div class="name">{{ op.name }}</div>
                <UiChip :variant="dispositionVariant(op.effectiveOpinion)">
                  {{ dispositionLabel(op.effectiveOpinion) }}
                </UiChip>
              </div>
              <div class="text-muted stat-mono num">
                <span>{{ op.areaId }}</span>
                <template v-if="op.locationId">
                  <span class="dot">•</span>
                  <span>{{ locationLabel(op.locationId) }}</span>
                </template>
                <template v-if="!canInteract(op)">
                  <span class="dot">•</span>
                  <span class="text-muted">not nearby</span>
                </template>
              </div>
            </div>
          </div>

          <UiProgressBar :value="clampOpinionPercent(op.effectiveOpinion)" tone="social" />

          <div class="kv-grid">
            <div class="kv">
              <div class="kv-label">NPC opinion</div>
              <div class="kv-value stat-mono num">{{ formatSigned(op.opinion) }}</div>
            </div>
            <div class="kv">
              <div class="kv-label">Area rep</div>
              <div class="kv-value stat-mono num">{{ formatSigned(op.areaReputation) }}</div>
            </div>
            <div class="kv">
              <div class="kv-label">Bribe</div>
              <div class="kv-value stat-mono num">{{ formatNumber(op.bribeCost) }} gp</div>
            </div>
          </div>

          <div class="actions">
            <template v-if="canInteract(op)">
              <UiButton
                size="sm"
                tone="social"
                variant="secondary"
                :disabled="actionBusy || activeAction?.npcId === op.npcId"
                @click="startTalk(op.npcId)"
              >
                <template #icon>{{ ICONS.actions.dice }}</template>
                Influence
              </UiButton>
              <UiButton
                v-if="activeAction?.npcId === op.npcId"
                size="sm"
                tone="social"
                variant="secondary"
                :disabled="actionBusy"
                @click="queueBribe(op.npcId)"
              >
                <template #icon>{{ ICONS.actions.key }}</template>
                Queue bribe
              </UiButton>
              <UiButton size="sm" variant="ghost" :disabled="loading || actionBusy" @click="adjust(op.npcId, -2)">
                Irritate <span class="stat-mono num">-2</span>
              </UiButton>
            </template>
            <template v-else>
              <UiButton
                v-if="op.locationId"
                size="sm"
                tone="social"
                variant="secondary"
                :disabled="actionBusy || loading"
                @click="enter(op.locationId)"
              >
                Visit {{ locationLabel(op.locationId) }}
              </UiButton>
              <UiButton v-else size="sm" variant="secondary" :disabled="actionBusy || loading" @click="exitBuilding">
                Leave building
              </UiButton>
              <UiChip variant="blocked">Visit the right location</UiChip>
            </template>
          </div>

          <UiPanel
            v-if="activeAction?.npcId === op.npcId"
            class="action-panel"
            tone="social"
            etched
          >
            <UiSectionHeader>
              <template #title>{{ activeAction.kind === 'bribe' ? 'Bribe' : 'Talking' }}</template>
              <template #actions>
                <UiChip variant="muted" class="stat-mono num">{{ etaCopy }}</UiChip>
              </template>
            </UiSectionHeader>
            <UiProgressBar :value="progressPercent" tone="social" />
            <div class="text-muted stat-mono num" style="margin-top: 0.45rem;">{{ etaCopy }}</div>
          </UiPanel>
        </div>
      </div>
    </UiPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { SocialActionState, SocialOpinionEntry } from "@idle-rpg/shared";
import {
  apiAdjustSocialOpinion,
  apiEnterLocation,
  apiExitLocation,
  apiQueueSocialBribe,
  apiStartSocialTalk
} from "../../api/client";
import { useSessionSocket } from "../../composables/useSessionSocket";
import { useToast } from "../../composables/useToast";
import { ICONS } from "../../lib/icons";
import { formatNumber } from "../../lib/format";
import UiButton from "../ui/UiButton.vue";
import UiCallout from "../ui/UiCallout.vue";
import UiChip from "../ui/UiChip.vue";
import UiPanel from "../ui/UiPanel.vue";
import UiProgressBar from "../ui/UiProgressBar.vue";
import UiSectionHeader from "../ui/UiSectionHeader.vue";

const props = defineProps<{
  token: string;
  characterId: string;
  currentLocationId: string | null;
  locations: Array<{ id: string; name: string }>;
  focusNpcId?: string | null;
  focusNpcStateId?: string | null;
  onlyNpcId?: string | null;
  onlyNpcStateId?: string | null;
}>();

const emit = defineEmits<{ (e: "location-changed"): void }>();
const { addToast } = useToast();
const sessionSocket = useSessionSocket();

const opinions = ref<SocialOpinionEntry[]>([]);
const loading = ref(false);
const refreshing = ref(false);
const error = ref<string | null>(null);
const actionBusy = ref(false);
const activeAction = ref<SocialActionState | null>(null);
const query = ref("");
const nowMs = ref(Date.now());
const lastUpdatedAt = ref<number | null>(null);
const SOCIAL_STALE_MS = 2 * 60 * 1000;
let wsUnsubscriber: (() => void) | null = null;
let nowTimer: number | null = null;
let actionRefreshTimer: number | null = null;

function stopTimers() {
  if (nowTimer !== null) {
    window.clearInterval(nowTimer);
    nowTimer = null;
  }
  if (actionRefreshTimer !== null) {
    window.clearTimeout(actionRefreshTimer);
    actionRefreshTimer = null;
  }
}

function formatSigned(n: number) {
  const v = Math.floor(Number(n || 0));
  if (v > 0) return `+${formatNumber(v)}`;
  if (v < 0) return `${v}`;
  return "0";
}

function clampOpinionPercent(v: number) {
  return Math.min(100, Math.max(0, ((v + 100) / 200) * 100));
}

function dispositionLabel(v: number) {
  if (v <= -75) return "Hostile";
  if (v >= 25) return "Friendly";
  return "Neutral";
}

function dispositionVariant(v: number) {
  if (v <= -75) return "danger" as const;
  if (v >= 25) return "active" as const;
  return "muted" as const;
}

async function loadSocialSnapshot(options?: { showLoading?: boolean }) {
  const showLoading = options?.showLoading ?? true;
  if (showLoading) {
    loading.value = true;
  } else {
    refreshing.value = true;
  }
  error.value = null;
  try {
    const snapshot = await sessionSocket.requestSnapshot("social");
    opinions.value = snapshot.data.opinions ?? [];
    if (snapshot.data.status?.result) {
      const tone = snapshot.data.status.result.opinionDelta >= 0 ? "success" : "warning";
      addToast(snapshot.data.status.result.message, tone);
    }
    activeAction.value = snapshot.data.status?.active ?? null;
    lastUpdatedAt.value = Date.now();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    error.value = message || "Failed to load relationships. Please retry.";
  } finally {
    if (showLoading) {
      loading.value = false;
    } else {
      refreshing.value = false;
    }
  }
}

const visibleOpinions = computed(() => {
  if (props.onlyNpcStateId) return opinions.value.filter((o) => o.npcStateId === props.onlyNpcStateId);
  if (props.onlyNpcId) return opinions.value.filter((o) => o.npcId === props.onlyNpcId);
  return opinions.value;
});

const filteredOpinions = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return visibleOpinions.value;
  return visibleOpinions.value.filter((o) => {
    const hay = [o.name, o.areaId, o.locationId || "", locationLabel(o.locationId || "")].join(" ").toLowerCase();
    return hay.includes(q);
  });
});

const opinionsFreshness = computed(() => {
  if (loading.value || refreshing.value) return { variant: "muted" as const, label: "Syncing" };
  if (lastUpdatedAt.value) {
    const stale = Date.now() - lastUpdatedAt.value > SOCIAL_STALE_MS;
    if (stale) return { variant: "warning" as const, label: "Stale" };
  }
  return { variant: "muted" as const, label: formatNumber(visibleOpinions.value.length) };
});

function locationLabel(locationId: string) {
  return props.locations.find((l) => l.id === locationId)?.name || locationId;
}

function canInteract(op: SocialOpinionEntry) {
  return (op.locationId || null) === (props.currentLocationId || null);
}

async function enter(locationId: string) {
  const res = await apiEnterLocation(props.token, props.characterId, { locationId });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  addToast(`Entered ${locationLabel(locationId)}.`, "info");
  emit("location-changed");
  await loadSocialSnapshot({ showLoading: false });
}

async function exitBuilding() {
  const res = await apiExitLocation(props.token, props.characterId);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  addToast("Exited building.", "info");
  emit("location-changed");
  await loadSocialSnapshot({ showLoading: false });
}

async function adjust(npcId: string, delta: number) {
  const res = await apiAdjustSocialOpinion(props.token, props.characterId, npcId, delta);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  addToast(`Opinion ${delta > 0 ? "raised" : "lowered"}`, delta > 0 ? "success" : "info");
  await loadSocialSnapshot({ showLoading: false });
}

async function startTalk(npcId: string) {
  actionBusy.value = true;
  const res = await apiStartSocialTalk(props.token, props.characterId, npcId);
  actionBusy.value = false;
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadSocialSnapshot({ showLoading: false });
}

async function queueBribe(npcId: string) {
  if (!activeAction.value) return;
  actionBusy.value = true;
  const res = await apiQueueSocialBribe(props.token, props.characterId, npcId);
  actionBusy.value = false;
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadSocialSnapshot({ showLoading: false });
}

const remainingMs = computed(() => {
  if (!activeAction.value) return 0;
  const completesAt = new Date(activeAction.value.completesAt).getTime();
  return Math.max(0, completesAt - nowMs.value);
});

const progressPercent = computed(() => {
  if (!activeAction.value) return 0;
  const startedAt = new Date(activeAction.value.startedAt).getTime();
  const completesAt = new Date(activeAction.value.completesAt).getTime();
  const total = completesAt - startedAt;
  if (total <= 0) return 0;
  const elapsed = Math.max(0, Math.min(total, nowMs.value - startedAt));
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
});

const etaCopy = computed(() => {
  if (!activeAction.value) return "Idle";
  const seconds = Math.max(0, Math.ceil(remainingMs.value / 1000));
  return `${seconds}s remaining`;
});

onMounted(() => {
  sessionSocket.subscribe(["social"]);
  wsUnsubscriber = sessionSocket.onEvent("social", (msg) => {
    if (msg.event !== "social.updated") return;
    void loadSocialSnapshot({ showLoading: false });
  });
  void loadSocialSnapshot();
  handleVisibilityChange();
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  sessionSocket.unsubscribe(["social"]);
  wsUnsubscriber?.();
  wsUnsubscriber = null;
  stopTimers();
});

function handleVisibilityChange() {
  if (!document.hidden) {
    nowMs.value = Date.now();
    void loadSocialSnapshot({ showLoading: false });
  }
}

watch(
  () => activeAction.value,
  (next) => {
    stopTimers();
    if (!next) return;

    // Local-only timer: keep the progress bar responsive without WS polling.
    nowTimer = window.setInterval(() => {
      nowMs.value = Date.now();
    }, 250);

    // One-shot refresh shortly after completion to surface result + clear active action.
    const completesAt = new Date(next.completesAt).getTime();
    const delay = Math.max(250, Math.min(60_000, completesAt - Date.now() + 350));
    actionRefreshTimer = window.setTimeout(() => {
      void loadSocialSnapshot({ showLoading: false });
    }, delay);
  }
);

watch(
  () => props.focusNpcId,
  async (id) => {
    if (!id) return;
    await nextTick();
    const el = document.querySelector(`[data-npc-id="${CSS.escape(id)}"]`);
    if (el && "scrollIntoView" in el) (el as HTMLElement).scrollIntoView({ block: "center" });
  },
  { immediate: true }
);

watch(
  () => props.focusNpcStateId,
  async (id) => {
    if (!id) return;
    await nextTick();
    const el = document.querySelector(`[data-npc-state-id="${CSS.escape(id)}"]`);
    if (el && "scrollIntoView" in el) (el as HTMLElement).scrollIntoView({ block: "center" });
  },
  { immediate: true }
);
</script>

<style scoped>
.social-panel {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.controls {
  margin-top: var(--space-md);
  display: flex;
  gap: var(--space-md);
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
}

.search {
  min-width: 220px;
  flex: 1 1 260px;
}

.list {
  display: grid;
  gap: 0.75rem;
  margin-top: var(--space-sm);
}

.opinion-row {
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  padding: 0.85rem;
  display: grid;
  gap: 0.65rem;
}

.opinion-row.focused {
  border-color: color-mix(in srgb, var(--accent) 38%, var(--glass-border-0));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent) 16%, transparent);
}

.opinion-head {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 0.75rem;
  align-items: start;
}

.icon-well {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
}

.opinion-main {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
}

.opinion-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  flex-wrap: wrap;
}

.name {
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dot {
  opacity: 0.7;
  padding: 0 0.35rem;
}

.kv-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
}

.kv {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.6rem 0.65rem;
}

.kv-label {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  margin-top: 0.2rem;
  font-weight: 800;
}

.actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-panel {
  margin-top: 0.15rem;
}

@media (max-width: 760px) {
  .kv-grid {
    grid-template-columns: 1fr;
  }
}
</style>
